import { DataProxy, useMutation } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import UserAvatar from '@deltek/specification-client/core/Menus/UserAvatar'
import { Assignment } from '@deltek/specification-client/core/Models/assignment'
import { UpdateAssignmentDataInput } from '@deltek/specification-client/core/models/ProjectTeamInput'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { getNameInitials } from '@deltek/specification-client/core/Utilities/stringUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Button from '@material-ui/core/Button'
import Divider from '@material-ui/core/Divider'
import IconButton from '@material-ui/core/IconButton'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import { UpdateProjectTeamAssignmentMutation } from 'api/graphQL/mutations/project.mutations'
import { GetProjectTeamQuery } from 'api/graphQL/queries/project.queries'
import { MutationProjectTeamAssignmentResponse } from 'api/graphQL/types/project.types'
import clsx from 'clsx'
import { AllProjectResponse } from 'Models/project'
import ProjectTeamMemberPopoutContent from 'pages/ProjectsPage/ProjectsHomePage/components/ProjectTeamMemberPopoutContent'
import React, { useCallback } from 'react'
import { getProjectTeamName } from 'shared/utils/projectTeamUtils'

const useStyles = makeStyles((theme: Theme) => ({
    headingText: {
        borderBottom: `3px solid ${theme.palette.common.white}`,
        width: 'fit-content',
        fontWeight: 500,
        lineHeight: pxToRem(20),
        textTransform: 'capitalize',
    },
    infoBlock: {
        wordBreak: 'break-word',
        '& ..MuiSvgIcon-fontSizeSmall': {
            fontSize: '3rem',
        },
        overflowY: 'auto',
        height: 252,
    },
    infoLabel: {
        fontSize: pxToRem(13),
        fontWeight: 500,
        textTransform: 'capitalize',
        lineHeight: pxToRem(15),
    },
    deleteIcon: {
        color: '#EB5757',
        width: 24,
        height: 24,
        cursor: 'pointer',
        margin: 'auto',
    },
    avatarItem: {
        flexDirection: 'column',
        display: 'flex',
        justifyContent: 'center',
        '& .avatarIcon': {
            width: '38px',
            height: '40px',
            margin: '0px',
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.common.white,
        },
        '& .spec-image': {
            border: '1px solid #FBB41F',
            width: '38px',
            height: '40px',
            margin: '0px',
        },
    },
    userDiscipline: {
        fontSize: pxToRem(13),
        fontWeight: 400,
        lineHeight: pxToRem(15),
    },
    membersList: {
        paddingTop: theme.spacing(2),
    },
    centerGridIcon: {
        display: 'grid',
        alignItems: 'center',
    },
    divider: {
        width: '100%',
        border: '1px solid rgba(255, 255, 255, 0.25)',
    },
    addTeamMemberButton: {
        borderRadius: pxToRem(100),
        background: theme.palette.common.white,
        color: theme.palette.common.black,
        '&:hover': {
            backgroundColor: theme.palette.common.white,
        },
        fontSize: pxToRem(13),
        margin: 'auto',
    },
    disabledAddMemberButton: {
        color: '#969390',
    },
    addTeamMemberCont: {
        height: pxToRem(68),
    },

    forwardIcon: {
        cursor: 'pointer',
    },
    userNameCont: {
        lineHeight: pxToRem(15),
        marginTop: 0,
        marginBottom: 0,
        flexDirection: 'column',
        display: 'flex',
        justifyContent: 'center',
    },
    centerGridItem: {
        margin: 'auto',
    },
}))

interface IdNameProps {
    id: string
    name: string
    roles: string[]
}
interface ProjectTeamMemberListProps {
    projectTeam?: null | string
    projectTeamUserList?: Partial<Assignment>[]
    selectedAssignment?: Partial<Assignment> | null
    setSelectedAssignment?: (assignment: Partial<Assignment> | null) => void
    setOpenProjectTeamDialog?: (
        isProjectTeamDialogOpen: boolean,
        variant: 'add' | 'remove'
    ) => VoidFunction
    handleTeamMemberDelete?: (currentAssignment: Partial<Assignment>) => void
    handlePopoutClose?: VoidFunction
    firmId?: string
    projectId?: string
    variant?: 'readOnly' | 'full'
    professionalDisciplines?: Partial<IdNameProps>[]
    setContentInEditMode?: (editMode: boolean) => void
    disciplineMemberCount?: number
}
const ProjectTeamMembersList: React.FC<ProjectTeamMemberListProps> = ({
    projectTeam,
    projectTeamUserList,
    selectedAssignment,
    setSelectedAssignment,
    setOpenProjectTeamDialog,
    handleTeamMemberDelete,
    handlePopoutClose,
    firmId,
    projectId,
    variant,
    professionalDisciplines,
    setContentInEditMode,
    disciplineMemberCount,
}) => {
    const classes = useStyles()

    const handleForwardArrowClick = (
        projectTeamMember: Partial<Assignment>
    ) => (_: React.MouseEvent) => {
        setSelectedAssignment?.(projectTeamMember)
    }

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [
        updateAssignment,
        { error: updateAssignmentError, loading: updateAssignmentLoading },
    ] = useMutation<
        { updateProjectTeamAssignment: Partial<Assignment> | null },
        { updateAssignmentDataInput: UpdateAssignmentDataInput }
    >(UpdateProjectTeamAssignmentMutation, {
        update: (
            cache: DataProxy,
            result: MutationProjectTeamAssignmentResponse
        ) => {
            if (!!result.data) {
                const getProjectOldData: Partial<
                    AllProjectResponse
                > | null = cache.readQuery<AllProjectResponse>({
                    query: GetProjectTeamQuery,
                    returnPartialData: true, // bug - 21704
                    variables: {
                        projectId: projectId,
                    },
                })
                cache.writeQuery({
                    query: GetProjectTeamQuery,
                    variables: {
                        projectId: projectId,
                    },
                    data: {
                        getProject: {
                            ...getProjectOldData?.getProject,
                            projectTeam: {
                                ...getProjectOldData?.getProject?.projectTeam!,
                                assignments: getProjectOldData?.getProject?.projectTeam?.assignments?.map(
                                    (assignedUser: Partial<Assignment>) => {
                                        if (
                                            assignedUser?.user?.id ===
                                            result?.data
                                                ?.updateProjectTeamAssignment
                                                ?.user?.id
                                        ) {
                                            return {
                                                ...assignedUser,
                                                description:
                                                    result?.data
                                                        ?.updateProjectTeamAssignment
                                                        ?.description,
                                                projectRole:
                                                    result?.data
                                                        ?.updateProjectTeamAssignment
                                                        ?.projectRole,
                                                user: { ...assignedUser.user },
                                            }
                                        } else return assignedUser
                                    }
                                ),
                            },
                        },
                    },
                })
            }
        },
    })

    const updateProjectAssignment = (
        projectRole: string,
        description: string,
        userId: string
    ) => {
        const updateAssignmentDataInput: UpdateAssignmentDataInput = {
            description: description,
            projectRole: projectRole,
            firmId: firmId!,
            userId: userId,
            projectId: projectId,
        }
        setSelectedAssignment?.({
            ...selectedAssignment,
            description: description,
            projectRole: projectRole,
        })
        return updateAssignment({
            variables: { updateAssignmentDataInput },
        })
    }

    const handleRemoveTeamMember = (projectTeamMember: Partial<Assignment>) => (
        _: React.MouseEvent
    ) => {
        handleTeamMemberDelete?.(projectTeamMember)
    }

    const getRoles = useCallback(
        (disciplineName: string): Record<'name' | 'value', string>[] => {
            const disciplineData = (professionalDisciplines || []).find(
                (discipline) => discipline.name === disciplineName
            )
            if (!!disciplineData) {
                const data = disciplineData.roles || []
                return data.map((value) => ({ name: value, value: value }))
            }
            return []
        },
        [professionalDisciplines]
    )
    React.useEffect(() => {
        if (updateAssignmentError) {
            showGlobalSnackbar({
                variables: {
                    id: 'manageProjectTeamPopup_updateAssignmentError',
                    variant: 'error',
                    message: 'Error while updating project team.',
                },
            })
        }
    }, [showGlobalSnackbar, updateAssignmentError])
    return (
        <>
            {!selectedAssignment ? (
                <>
                    <Typography
                        data-testid="projectTeamMebersTitle"
                        variant="h5"
                        className={classes.headingText}
                    >
                        {`${
                            projectTeam
                                ? getProjectTeamName(projectTeam)
                                : 'Default'
                        } Team Members`}
                    </Typography>

                    <GridContainer
                        className={classes.infoBlock}
                        direction="column"
                    >
                        <GridItem
                            className={
                                !projectTeamUserList?.length
                                    ? classes.centerGridItem
                                    : ''
                            }
                        >
                            {projectTeamUserList?.length ? (
                                projectTeamUserList.map(
                                    (
                                        projectTeamMember: Partial<Assignment>,
                                        index: number
                                    ) => {
                                        if (!!projectTeamMember?.user)
                                            return (
                                                <GridContainer
                                                    className={
                                                        classes.membersList
                                                    }
                                                    key={index}
                                                >
                                                    <GridItem
                                                        xs={2}
                                                        className={
                                                            classes.avatarItem
                                                        }
                                                    >
                                                        <UserAvatar
                                                            userInitials={getNameInitials(
                                                                projectTeamMember
                                                                    .user
                                                                    .profileInfo
                                                                    ?.firstName ??
                                                                    '',
                                                                projectTeamMember
                                                                    .user
                                                                    .profileInfo
                                                                    ?.lastName ??
                                                                    ''
                                                            )}
                                                            userProfileImage={
                                                                projectTeamMember
                                                                    .user
                                                                    .profileInfo
                                                                    ?.imageUrl
                                                            }
                                                        />
                                                    </GridItem>
                                                    <GridItem
                                                        xs={7}
                                                        className={
                                                            classes.userNameCont
                                                        }
                                                    >
                                                        <>
                                                            <Typography
                                                                className={clsx(
                                                                    classes.infoLabel,
                                                                    'projectGroupLabel'
                                                                )}
                                                                variant="body2"
                                                                component="span"
                                                            >
                                                                {(projectTeamMember
                                                                    .user
                                                                    .profileInfo
                                                                    ?.firstName +
                                                                    ' ' ?? '') +
                                                                    (projectTeamMember
                                                                        .user
                                                                        .profileInfo
                                                                        ?.lastName ??
                                                                        '')}
                                                            </Typography>
                                                            <Typography
                                                                className={
                                                                    classes.userDiscipline
                                                                }
                                                                variant="body2"
                                                            >
                                                                {projectTeamMember
                                                                    .user
                                                                    ?.profileInfo
                                                                    ?.professionalDiscipline +
                                                                    ', ' +
                                                                    (projectTeamMember.projectRole ??
                                                                        projectTeamMember
                                                                            .user
                                                                            ?.profileInfo
                                                                            ?.professionalRole)}
                                                            </Typography>
                                                        </>
                                                    </GridItem>
                                                    <GridItem
                                                        xs={2}
                                                        className={
                                                            classes.centerGridIcon
                                                        }
                                                    >
                                                        <IconButton
                                                            data-testid="deleteTeamMemberIcon"
                                                            onClick={handleRemoveTeamMember(
                                                                projectTeamMember
                                                            )}
                                                            disabled={
                                                                variant !==
                                                                'full'
                                                            }
                                                            className={
                                                                classes.deleteIcon
                                                            }
                                                        >
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </GridItem>
                                                    <GridItem
                                                        xs={1}
                                                        className={
                                                            classes.centerGridIcon
                                                        }
                                                    >
                                                        <ArrowForwardIosIcon
                                                            data-testid="projectTeamViewInfoArrow"
                                                            fontSize="small"
                                                            onClick={handleForwardArrowClick(
                                                                projectTeamMember
                                                            )}
                                                            className={
                                                                classes.forwardIcon
                                                            }
                                                        />
                                                    </GridItem>
                                                </GridContainer>
                                            )
                                        else {
                                            return <></>
                                        }
                                    }
                                )
                            ) : (
                                <>
                                    <GridItem
                                        className={classes.centerGridIcon}
                                        xs={12}
                                    >
                                        <Typography>
                                            No Team Members Added
                                        </Typography>
                                    </GridItem>
                                </>
                            )}
                        </GridItem>
                    </GridContainer>
                    <GridContainer>
                        <GridItem xs={12}>
                            <Divider
                                variant="fullWidth"
                                className={classes.divider}
                            />
                        </GridItem>
                    </GridContainer>
                    <GridContainer className={classes.addTeamMemberCont}>
                        <GridItem xs={12} className={classes.centerGridIcon}>
                            <Button
                                variant="outlined"
                                className={clsx(
                                    'addTeamMember',
                                    classes.addTeamMemberButton
                                )}
                                onClick={setOpenProjectTeamDialog?.(
                                    true,
                                    'add'
                                )}
                                disabled={
                                    variant !== 'full' ||
                                    disciplineMemberCount === 0
                                }
                                startIcon={
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                    >
                                        <rect
                                            width="24"
                                            height="24"
                                            rx="12"
                                            fill="#A68966"
                                        />
                                        <path
                                            d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z"
                                            fill="white"
                                        />
                                    </svg>
                                }
                            >
                                Add Team Member
                            </Button>
                        </GridItem>
                    </GridContainer>
                </>
            ) : (
                <ProjectTeamMemberPopoutContent
                    userWithProjectRole={{
                        isAssignedToProject: true,
                        projectRole:
                            selectedAssignment.projectRole ??
                            selectedAssignment?.user?.profileInfo
                                ?.professionalRole ??
                            '',
                        user: selectedAssignment.user!,
                    }}
                    assignment={selectedAssignment}
                    setSelectedAssignment={setSelectedAssignment}
                    handlePopoutClose={handlePopoutClose!}
                    updateAssignment={updateProjectAssignment}
                    updateAssignmentLoading={updateAssignmentLoading}
                    getRoles={getRoles}
                    showBackButton={true}
                    canEdit={variant === 'full'}
                    setContentInEditMode={setContentInEditMode}
                />
            )}
        </>
    )
}

export default ProjectTeamMembersList
